import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Tent, Menu, X, Phone } from "lucide-react";
import { CONTACT, BOOKING_URL, FLOWER_LOGO } from "../lib/data";

const LINKS = [
  ["Le Camping", "#camping"],
  ["Galerie", "#galerie"],
  ["Hébergements", "#hebergements"],
  ["Randonnées", "route:/randonnees"],
  ["Piscine", "#piscine"],
  ["Services", "#services"],
  ["La Région", "#region"],
  ["FAQ", "#faq"],
];

export default function Navbar({ scrollTo }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("route:")) {
      navigate(href.slice(6));
      return;
    }
    scrollTo(href);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="navbar">
      <div className="mx-3 sm:mx-6 mt-3 rounded-2xl bg-sand/85 backdrop-blur-xl border border-ink/10 shadow-lg shadow-ink/5">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16">
          <button
            onClick={() => go("#top")}
            className="flex items-center gap-2.5 group"
            data-testid="nav-logo"
          >
            <span className="grid place-items-center w-9 h-9 rounded-full bg-pine text-sand transition-transform duration-300 group-hover:-rotate-12">
              <Tent size={18} strokeWidth={2.2} />
            </span>
            <span className="text-left leading-none">
              <span className="block font-display font-bold text-lg text-ink">Le Lac aux Oiseaux</span>
              <span className="block text-[10px] tracking-[0.22em] uppercase text-pine font-semibold mt-0.5">
                Camping ★★★★ · Aveyron
              </span>
            </span>
            <img
              src={FLOWER_LOGO}
              alt="Membre du réseau Flower Campings"
              className="hidden md:block h-9 w-auto ml-3 pl-3 border-l border-ink/10"
              data-testid="nav-flower-logo"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-5">
            {LINKS.map(([label, href]) => (
              <button
                key={href}
                onClick={() => go(href)}
                data-testid={`nav-link-${href.startsWith("route:") ? href.slice(7) : href.slice(1)}`}
                className="text-[13px] font-medium text-ink/70 hover:text-pine transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-0 after:bg-terra after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-phone-link"
              className="hidden md:flex items-center gap-2 text-sm font-semibold text-lake hover:text-pine transition-colors duration-300 mr-2"
            >
              <Phone size={15} /> {CONTACT.phone}
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-reserver"
              className="rounded-full bg-terra text-sand text-sm font-semibold px-5 py-2.5 shadow-md shadow-terra/30 transition-all duration-300 hover:bg-pine hover:shadow-pine/30 hover:-translate-y-0.5"
            >
              Réserver
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-menu-toggle"
              className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-ink/10 text-ink"
              aria-label="Menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden overflow-hidden border-t border-ink/10"
            >
              <div className="flex flex-col px-6 py-4 gap-1">
                {LINKS.map(([label, href]) => (
                  <button
                    key={href}
                    onClick={() => go(href)}
                    data-testid={`nav-mobile-link-${href.startsWith("route:") ? href.slice(7) : href.slice(1)}`}
                    className="text-left py-2.5 font-medium text-ink/80 hover:text-pine transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
